import {
	ActionReducerMapBuilder,
	createAsyncThunk,
	createSlice,
	PayloadAction
} from '@reduxjs/toolkit'
import {
	IRequestConfigsState,
	IRequestLocationState,
	IRequestModelState,
	IRequestState,
	IRequestCreatedState
} from '@services/store/request/type'
import { IThunk } from '@services/store/type'
import { IUser } from '@services/types'
import {
	Address,
	Request,
	AddressCoordinates,
	RequestConfigs,
	User,
	RequestNumericTransaction
} from '@services/models'
import {
	requestCategoryDic,
	requestSubCategoryDic,
	requestSubCategoryDicID
} from '@services/constants'
import { RequestInputFiles, TNRequestNames } from '@utils/request'
import { logFrontendEvent } from '@services/logger/frontend-logging'

const initialState: IRequestState = {
	requestCreated: {
		code: '',
		id: ''
	},
	requestFailed: false,
	model: {
		title: '',
		typeId: '',
		name: '',
		description: '',
		customerId: '',
		metadata: [],
		files: []
	},
	location: {
		useDefaultUserLocation: true,
		city: '',
		address: '',
		apartment: '',
		postalCode: '',
		coordinates: {}
	},
	configs: {} as IRequestConfigsState,
	listRequestIdsForArticles: []
}

export const slice = createSlice({
	name: 'createRequest',
	initialState,
	reducers: {
		populateRequestModelState: (
			state,
			action: PayloadAction<Partial<IRequestModelState>>
		) => {
			Object.assign(state.model, action.payload)
		},
		populateRequestConfigsState: (
			state,
			action: PayloadAction<Partial<IRequestConfigsState>>
		) => {
			Object.assign(state.configs, action.payload)
		},
		setRequestConfigsState: (state, action) => {
			state.configs = action.payload
		},
		populateRequestLocationState: (
			state,
			action: PayloadAction<Partial<IRequestLocationState>>
		) => {
			Object.assign(state.location, action.payload)
		},
		clearRequestState: (state) => {
			state.requestCreated = initialState.requestCreated
			state.requestFailed = initialState.requestFailed
			state.configs = initialState.configs
			state.location = initialState.location
			state.model = initialState.model
			state.listRequestIdsForArticles = initialState.listRequestIdsForArticles
		},
		resetRequestConfigsState: (state) => {
			state.configs = initialState.configs
		},
		populateArticlesRequestId: (state, action: PayloadAction<string[]>) => {
			state.listRequestIdsForArticles = action.payload
		},
		resetRequestFailedState: (state, action: PayloadAction<boolean>) => {
			state.requestFailed = action.payload
		}
	},
	extraReducers: (builder: ActionReducerMapBuilder<IRequestState>) => {
		builder
			.addCase(createRequest.fulfilled, (state, { payload }) => {
				state.requestCreated = payload
			})
			.addCase(createRequest.rejected, (state) => {
				state.requestFailed = true
			})
	}
})

export const createRequest = createAsyncThunk<
	IRequestCreatedState,
	User,
	IThunk
>('request/create', async (authUser, thunkAPI) => {
	const state = thunkAPI.getState()

	const request: Request = new Request(authUser as IUser)
	const location: Address = new Address()
	const coordinates: AddressCoordinates = new AddressCoordinates()
	const configs: RequestConfigs = new RequestConfigs()
	// const dispatch = useAppDispatch()

	coordinates.update(state.request.location.coordinates)

	if (!coordinates.validate()) {
		logFrontendEvent({
			description: 'Request creation failed due to invalid coordinates',
			action: 'Create request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload: 'Coordinates can not be empty : ' + state.request.model.name+ ' - location: ' + JSON.stringify(state.request.location)
		})
		throw new Error('coordinates can not be empty')
	}

	location.update({
		...state.request.location,
		coordinates
	})

	configs.update(state.request.configs)

	request.update({
		...state.request.model,
		configs,
		location
	})

	try {
		await request.saveAsync()
	} catch (error) {
		logFrontendEvent({
			description: 'Request save failed',
			action: 'Create request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload:
				error instanceof Error
					? error.message
					: 'Unknown error during saveAsync'
		})
		throw error
	}

	try {
		await request.saveAttachmentAsync()
	} catch (error) {
		logFrontendEvent({
			description: 'Request attachment save failed',
			action: 'Create request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload:
				error instanceof Error
					? error.message
					: 'Unknown error during saveAttachmentAsync'
		})
		throw error
	}

	// const requestsList = await authUser.fetchRequests()
	const myRequest = await authUser.fetchRequest(`${request.id}`)

	// store.dispatch(
	// 	setRequestListStore({
	// 		fetching: false,
	// 		fetched: true,
	// 		requests: requestsList
	// 	})
	// )

	// const requestFound: Request | undefined = authUser.getRequest(`${request.id}`)

	return {
		id: `${request.id}`,
		code: `${myRequest?.code}`
	}
})

export const createTNRequest = createAsyncThunk<
	IRequestCreatedState,
	{ authUser: User; filesInputs: RequestInputFiles[] },
	IThunk
>('request/create', async ({ authUser, filesInputs }, thunkAPI) => {
	const state = thunkAPI.getState()

	const request: Request = new Request(authUser as IUser)
	const location: Address = new Address()
	const coordinates: AddressCoordinates = new AddressCoordinates()
	const configs: RequestConfigs = new RequestConfigs()
	// const dispatch = useAppDispatch()

	coordinates.update(state.request.location.coordinates)

	location.update({
		...state.request.location,
		coordinates
	})

	configs.update(state.request.configs)

	request.update({
		...state.request.model,
		configs,
		location
	})

	if (
		// les requêtes autorisées à soumission avec adresse en dehors de Laval
		![
			TNRequestNames.annualRegistrationCertificateForContractors,
			TNRequestNames.historicalArchive,
			TNRequestNames.hydraulicAndPressureTests,
			TNRequestNames.publicAuctions,
			TNRequestNames.fireHydrantsTankFilling,
			TNRequestNames.fireHydrantsTempAqueductNetwork,
			TNRequestNames.temporaryPesticideApplicationPermitForIndividuals,
			TNRequestNames.permitOccupationOfPublicHighway,
			TNRequestNames.claimNotice,
			TNRequestNames.municipalEvaluation,
			TNRequestNames.carSharing,
			TNRequestNames.replacementOilHeatingSystem,
			TNRequestNames.treePlanting,
			TNRequestNames.sustainableHygieneProducts,
			TNRequestNames.bikeSharing,
			TNRequestNames.buildingTransactionRequest,
			TNRequestNames.rainwaterBarrel,
			TNRequestNames.crackedHouses,
			TNRequestNames.heritageBuildingsRevitalizationProgram,
			TNRequestNames.fireplaceDeclaration,
			TNRequestNames.selfReliefWaterCounter,
			TNRequestNames.oilHeatingDeclaration,
			TNRequestNames.fireplaceGrant,
			TNRequestNames.parkingPermit,
			TNRequestNames.swimmingPoolSpaPermit,
			TNRequestNames.buildingConstructionOrAdditionPermit,
			TNRequestNames.wateringPermit,
			TNRequestNames.buildingEnlargementPermit,
			TNRequestNames.residentialRenovationOrRepairPermit,
			TNRequestNames.landDevelopmentWorkPermit,
			TNRequestNames.buildingDemolitionOrMovingPermit,
			TNRequestNames.septicTank,
			TNRequestNames.leisureTarifsReduction,
			TNRequestNames.collectBulkyItems,
			TNRequestNames.annualPermitAndTagForAnimals,
			TNRequestNames.clothDiapers,
			TNRequestNames.elderSubventionRenewal
		].includes(String(request.name)) &&
		!coordinates.validate()
	) {
		logFrontendEvent({
			description: 'Request creation failed due to invalid coordinates',
			action: 'Create request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload: 'Coordinates can not be empty: ' + String(request.name) + ' - location: ' + JSON.stringify(state.request.location)
		})
		throw new Error('coordinates can not be empty: ' + String(request.name))
	}

	try {
		await request.saveAsyncTN()
	} catch (error) {
		logFrontendEvent({
			description: 'TN Request save failed',
			action: 'Create TN request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload:
				error instanceof Error
					? error.message
					: 'Unknown error during saveAsyncTN'
		})
		throw error
	}

	const attachmentErrors: string[] = []

	for (let input of filesInputs) {
		if (!!input.separateFiles) {
			const keys = Object.keys(input.separateFiles!)
			for (let key of keys) {
				const NT = new RequestNumericTransaction({
					incidentId: '',
					incidentFormId: '',
					category: requestCategoryDic[input.category || input.name],
					sub_category: requestSubCategoryDic[input.subCategory || input.name],
					description: input.description || key,
					subject: key
				})

				NT.incidentId = request.id!
				NT.incidentFormId = request.formId!
				NT.file = [...input.separateFiles![key]][0]

				try {
					await NT.saveAsyncNumericTransaction()
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: 'Unknown error during saveAsyncNumericTransaction'
					logFrontendEvent({
						description: 'TN Numeric Transaction save failed',
						action: 'Create TN request',
						id: authUser?.profile?.id || 'request-creation',
						username: authUser?.profile?.email || '',
						step: 'Error',
						payload: errorMessage
					})
					attachmentErrors.push(
						`${input.name || 'unknown-input'}:${key}:[file:${
							NT.file?.name || 'unknown-file'
						}] numeric transaction - ${errorMessage}`
					)
					continue
				}

				try {
					await NT.saveTNAttachment()
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: 'Unknown error during saveTNAttachment'
					logFrontendEvent({
						description: 'TN Attachment save failed',
						action: 'Create TN request',
						id: authUser?.profile?.id || 'request-creation',
						username: authUser?.profile?.email || '',
						step: 'Error',
						payload: errorMessage
					})
					attachmentErrors.push(
						`${input.name || 'unknown-input'}:${key}:[file:${
							NT.file?.name || 'unknown-file'
						}] attachment - ${errorMessage}`
					)
				}
			}
		} else if (input.files.size > 0 || input.files.length > 0) {
			for (let file of input.files) {
				const NT = new RequestNumericTransaction({
					incidentId: '',
					incidentFormId: '',
					category: requestCategoryDic[input.category || input.name],
					sub_category: requestSubCategoryDic[input.subCategory || input.name],
					description: input.description || input.label,
					subject: input.label || ' ',
					document_reference: input.uploadedFilesMap
						? input.uploadedFilesMap.get(file.name)
						: undefined
				})

				NT.incidentId = request.id!
				NT.incidentFormId = request.formId!
				if (!NT.document_reference) {
					// si le fichier n'est pas un document de référence
					NT.file = file
				}

				try {
					await NT.saveAsyncNumericTransaction()
				} catch (error) {
					const errorMessage =
						error instanceof Error
							? error.message
							: 'Unknown error during saveAsyncNumericTransaction'
					logFrontendEvent({
						description: 'TN Numeric Transaction save failed',
						action: 'Create TN request',
						id: authUser?.profile?.id || 'request-creation',
						username: authUser?.profile?.email || '',
						step: 'Error',
						payload: errorMessage
					})
					attachmentErrors.push(
						`${input.name || 'unknown-input'}:${
							file.name
						} numeric transaction - ${errorMessage}`
					)
					continue
				}

				if (NT.file) {
					// save le fichier seulement si ce n'est pas un document de référence
					try {
						await NT.saveTNAttachment()
					} catch (error) {
						const errorMessage =
							error instanceof Error
								? error.message
								: 'Unknown error during saveTNAttachment'
						logFrontendEvent({
							description: 'TN Attachment save failed',
							action: 'Create TN request',
							id: authUser?.profile?.id || 'request-creation',
							username: authUser?.profile?.email || '',
							step: 'Error',
							payload: errorMessage
						})
						attachmentErrors.push(
							`${input.name || 'unknown-input'}:${
								file.name
							} attachment - ${errorMessage}`
						)
					}
				}
			}
		}
	}

	if (attachmentErrors.length > 0) {
		logFrontendEvent({
			description: 'TN request completed with attachment errors',
			action: 'Create TN request',
			id: authUser?.profile?.id || 'request-creation',
			username: authUser?.profile?.email || '',
			step: 'Error',
			payload: `Failed to upload ${attachmentErrors.length} file(s): ${attachmentErrors.join(' | ')}`
		})
		throw new Error(
			`Request created but some files failed: ${attachmentErrors.join(' | ')}`
		)
	}

	// const requestsList = await authUser.fetchRequests()
	const myRequest = await authUser.fetchRequest(`${request.id}`)

	// store.dispatch(
	// 	setRequestListStore({
	// 		fetching: false,
	// 		fetched: true,
	// 		requests: requestsList
	// 	})
	// )

	// const requestFound: Request | undefined = authUser.getRequest(`${request.id}`)

	return {
		id: `${request.id}`,
		code: `${myRequest?.code}`
	}
})

export const createTNUrbanRequest = createAsyncThunk<
	IRequestCreatedState,
	{ authUser: User; filesInputs: RequestInputFiles[]; summaryHtml: string },
	IThunk
>(
	'request/create',
	async ({ authUser, filesInputs, summaryHtml }, thunkAPI) => {
		const state = thunkAPI.getState()

		const request: Request = new Request(authUser as IUser)
		const location: Address = new Address()
		const coordinates: AddressCoordinates = new AddressCoordinates()
		const configs: RequestConfigs = new RequestConfigs()
		// const dispatch = useAppDispatch()

		coordinates.update(state.request.location.coordinates)

		location.update({
			...state.request.location,
			coordinates
		})

		configs.update(state.request.configs)

		request.update({
			...state.request.model,
			configs,
			location
		})

		if (
			// les requêtes autorisées à soumission avec adresse en dehors de Laval
			![
				TNRequestNames.swimmingPoolSpaPermit,
				TNRequestNames.buildingConstructionOrAdditionPermit,
				TNRequestNames.buildingEnlargementPermit,
				TNRequestNames.residentialRenovationOrRepairPermit,
				TNRequestNames.landDevelopmentWorkPermit,
				TNRequestNames.buildingDemolitionOrMovingPermit,
				TNRequestNames.industrialCommercialOrAgriculturalUsePermit
			].includes(String(request.name)) &&
			!coordinates.validate()
		) {
			console.error('coordinates can not be empty')
			throw new Error('coordinates can not be empty')
		}

		// Map attachements
		let attachements: any[] = []
		for (let input of filesInputs) {
			if (input.files.size > 0 || input.files.length > 0) {
				for (let file of input.files) {
					attachements.push({
						subject: input.label || ' ',
						description: input.description || input.label || ' ',
						category: requestCategoryDic[input.category || input.name],
						sub_category:
							requestSubCategoryDic[input.subCategory || input.name],
						sub_category_id:
							requestSubCategoryDicID[input.subCategory || input.name],
						document_reference: input.uploadedFilesMap
							? input.uploadedFilesMap.get(file.name)
							: undefined
					})
				}
			}
		}

		try {
			await request.saveAsyncTNUrban(attachements, summaryHtml)
		} catch (error) {
			logFrontendEvent({
				description: 'TN Urban request save failed',
				action: 'Create TN Urban request',
				id: authUser?.profile?.id || 'request-creation',
				username: authUser?.profile?.email || '',
				step: 'Error',
				payload:
					error instanceof Error
						? error.message
						: 'Unknown error during saveAsyncTNUrban'
			})
			throw error
		}

		// const requestsList = await authUser.fetchRequests()
		const myRequest = await authUser.fetchRequest(`${request.id}`)

		// store.dispatch(
		// 	setRequestListStore({
		// 		fetching: false,
		// 		fetched: true,
		// 		requests: requestsList
		// 	})
		// )

		// const requestFound: Request | undefined = authUser.getRequest(`${request.id}`)

		return {
			id: `${request.id}`,
			code: `${myRequest?.code}`
		}
	}
)

export default slice.reducer

export const {
	populateRequestModelState,
	populateRequestConfigsState,
	setRequestConfigsState,
	populateRequestLocationState,
	clearRequestState,
	resetRequestConfigsState,
	populateArticlesRequestId,
	resetRequestFailedState
} = slice.actions
